import React, { createRef, useEffect, useState } from 'react'
import { LicenseFormActions, licenseFormProps, LicenseFeatureVersion, IStmFairUsage, IStmFairUsageRatio, IStmFairUsageBand } from './LicenseForm.types'
import { Button, ButtonType, IToastNotification, Select, Switch, ToasterContainer } from '@digicert/dcone-common-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { Translate } from 'react-localize-redux'
import cn from 'classnames'
import { Tooltip } from 'antd'
import styles from './LicenseForm.module.scss'
import { translate } from 'shared/helpers/utils'
import HTTPUrlHelper from 'shared/helpers/HTTPUrlHelper'
import { Applications, IlicenseCard } from '../Tenant.types'
import { UNLIMITED } from 'core/constants'
import { IAccessScope } from 'reducers/Reducers.interfaces'

declare global {
  interface Window {
    licenseData: any
  }
}

export const LicenseForm = (props: licenseFormProps) => {
  const { dataSource, onCancelClick, updateLicenseByApp, createLicenseAllocationUpdateEvent, context, id, license, app_code, partnerAccountId, type, licenseFeatureVersion, showToggle, fairUsage, stmPackageCode } = props

  const isStmApp = app_code?.includes(Applications.SSM)
  const isTlmApp = app_code?.includes(Applications.MPKI)

  const versionMatches = (rowVersion: string | undefined, requested: string) => {
    if (!rowVersion) return false
    if (rowVersion === requested) return true
    // For STM v2, also include v2.5 dependent seats (shown as read-only greyed rows).
    if (isStmApp && requested === LicenseFeatureVersion.V2 && rowVersion === LicenseFeatureVersion.V2_5) return true
    // For TLM, domains (v2.6) is shown under license feature version v2 in BOTH toggle
    // states: toggle on (v2 tlm_seat) and toggle off (v2.5 users/sites).
    return isTlmApp && rowVersion === LicenseFeatureVersion.V2_6 &&
      (requested === LicenseFeatureVersion.V2 || requested === LicenseFeatureVersion.V2_5)
  }

  const getDataSource = () => {
    if (licenseFeatureVersion) {
      return dataSource.filter(license => versionMatches(license?.version, licenseFeatureVersion))
    }
    return dataSource
  }
  const dataSourceState = getDataSource()
  const accountRemainingArray = new Array<string>(dataSourceState.length).fill('-')
  const licenseRemainingArray = new Array<string>(dataSourceState.length).fill('-')
  const updateLimitArray = new Array<string>(dataSourceState.length)
  const licenseResetAllowedApps = ['signingmanager']
  const isLicenseResetAllowed = licenseResetAllowedApps.some((app) => props?.context?.includes(app));
  const actions = [
    {
      label: translate('common.noChange') as string,
      value: LicenseFormActions.NO_CHANGE
    },
    {
      label: translate('common.add') as string,
      value: LicenseFormActions.ADD
    },
    {
      label: translate('common.subtract') as string,
      value: LicenseFormActions.SUBTRACT
    }
  ]
  const defaultErrorMessages = new Array<string>(dataSourceState.length).fill('')
  const errorRefs = new Array<any>(dataSourceState.length)
  const hsmKeyPairFeatureId = 'hsm_keypair_units';
  // After V21.84, STM v2 features are stored with suffixes. Match both for compatibility.
  const isSignatureUnitsFeature = (fid: string) => fid === 'signature_units' || fid === 'signature_units_v2';
  const isHsmKeyPairFeature = (fid: string) => fid === 'hsm_keypair_units' || fid === 'hsm_keypair_units_v2';
  const isStm = isStmApp;

  // Return the fair-usage cap display string for STM v2.5 derived rows, or
  // null when it doesn't apply. Applied at render time so it survives any
  // licenseData state resets (e.g. after Update button reloads the form).
  const capDisplayForFeature = (featureId?: string): string | null => {
    if (!isStm || !fairUsage || !featureId) return null
    const n = featureId.toLowerCase()
    let cap: number | undefined
    if (n === 'repositories' || n === 'repositories_v25') cap = fairUsage.repositories?.cap
    else if (n === 'test_signatures' || n === 'test_signature_units' || n === 'test_signature_units_v25') cap = fairUsage.test_signatures?.cap
    if (cap === undefined) return null
    if (cap === -1) return translate('common.form.unlimited') as string
    return cap.toString()
  }

  // Normalize STM feature identifiers so the platform-licence lookup matches
  // regardless of whether the row uses base (`signature_units`) or suffixed
  // (`signature_units_v2`) names, and regardless of STM's two aliases for the
  // derived seats (`test_signatures` <-> `test_signature_units`,
  // `repositories` <-> `repositories_v25`).
  const normalizeFeatureName = (name?: string): string => {
    if (!name) return '';
    let n = name.toLowerCase();
    n = n.replace(/_v25$/, '').replace(/_v2$/, '');
    if (n === 'test_signatures') n = 'test_signature_units';
    return n;
  };
  const featureNameMatches = (a?: string, b?: string) => normalizeFeatureName(a) === normalizeFeatureName(b);
  // Prefer an exact (case-insensitive) match on the raw feature name to keep
  // v1 (`signature_units`) and v2 (`signature_units_v2`) entries separated when
  // both are present in the platform-licence feature list. Fall back to the
  // normalized match only when there is no exact hit — needed for STM's
  // derived seat aliases (`test_signatures` ↔ `test_signature_units`,
  // `repositories` ↔ `repositories_v25`).
  const findPlatformFeature = (features: any[] | undefined, rowFeatureId: string) => {
    if (!features?.length) return undefined;
    const target = (rowFeatureId || '').toLowerCase();
    const exact = features.find(f => (f?.name || '').toLowerCase() === target);
    if (exact) return exact;
    return features.find(f => featureNameMatches(f?.name, rowFeatureId));
  };

  for (const i in dataSourceState) {
    errorRefs[i] = createRef<HTMLParagraphElement>()
  }

  const [errorMessages, setErrorMessages] = useState(defaultErrorMessages)
  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [licenseData, setLicenseData] = useState(dataSourceState)
  const [accountRemaining, setAccountRemaining] = useState(accountRemainingArray)
  const [licenseRemaining, setLicenseRemaining] = useState(licenseRemainingArray)
  const [updateLimit, setUpdateLimit] = useState(updateLimitArray)
  const [action, setAction] = useState(new Array<string>(dataSourceState.length).fill('no-change'))
  const [inputValues, setInputValues] = useState(new Array<string>(dataSourceState.length).fill(''))
  const [selectedLicenseVersion, setSelectedLicenseVersion] = useState<LicenseFeatureVersion>(LicenseFeatureVersion.V1)
  const [parentLicenseData, setParentLicenseData] = useState(dataSource)
  const [v2Enabled, setV2Enabled] = useState(false)
  const [notifications, setNotifications] = useState<IToastNotification[]>([])
  const [platformLicense, setPlatformLicense] = useState<any>([])
  // Snapshot of the original STM HSM fair/addon (as returned by the account
  // usage API). Used so that when the user types in the HSM row, the addon is
  // computed as initialAddon +/- input, independent of any live fair changes
  // driven by the signature-units row.
  const initialHsmRef = React.useRef<{ fair: number; addon: number } | null>(null)
  // Snapshot of each row's initial derived value + platform allocation, keyed
  // by featureId. Used to (a) restore derived rows when the sig-units input is
  // cleared and (b) compute the platform-column delta after update.
  const initialByFeatureRef = React.useRef<Record<string, { remaining: number; platform: number | 'unlimited' | null }>>({})
  let inputRefs: Array<HTMLInputElement> = [];

  useEffect(() => {
    if (app_code) {
      if (partnerAccountId) {
        fetch(HTTPUrlHelper.getAccountLicenseByApp(app_code, partnerAccountId)).then(async (response: any) => {
          setupStates(response)
        })
      } else {
        fetch(HTTPUrlHelper.getAMLicenseByApp(app_code)).then(async (response: any) => {
          setupStates(response)
        })
      }
    }
  }, [app_code])

  // STM v2.5 derived rows (repositories, test_signatures): the platform-column
  // value comes from the fair-usage caps, not from the platform-license API.
  //   cap === -1 => Unlimited
  //   cap === 0  => 0 (none, e.g. Essentials test_signatures)
  //   cap > 0    => the cap value
  useEffect(() => {
    if (!isStm || !fairUsage) return
    const capFor = (fid?: string): number | undefined => {
      if (!fid) return undefined
      const n = fid.toLowerCase()
      if (n === 'repositories' || n === 'repositories_v25') return fairUsage.repositories?.cap
      if (n === 'test_signatures' || n === 'test_signature_units' || n === 'test_signature_units_v25') return fairUsage.test_signatures?.cap
      return undefined
    }
    const formatCap = (cap: number): string =>
      cap === -1 ? (translate('common.form.unlimited') as string) : cap.toString()
    setLicenseData(prev => prev.map((row: any) => {
      const cap = capFor(row.featureId)
      if (cap === undefined) return row
      // Invalidate any previously-captured snapshot for this row so the delta
      // baseline is recaptured from the cap-overridden value on the next pass.
      delete initialByFeatureRef.current[row.featureId]
      return { ...row, licenseAllocation: formatCap(cap) }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairUsage, platformLicense])

  // Capture the initial fair-usage/addon numbers for the STM HSM row from the
  // first render that has them. These are used later to compute addon deltas
  // when the user changes the HSM input, and to correctly re-derive the HSM
  // limit when the signature-units row triggers a fair-usage recalculation.
  useEffect(() => {
    if (initialHsmRef.current) return
    const hsmRow: any = licenseData.find(r => isHsmKeyPairFeature(r.featureId))
    if (hsmRow && (hsmRow.fairUsageHsmKeypairs != null || hsmRow.addonHsmKeypairs != null)) {
      initialHsmRef.current = {
        fair: Number(hsmRow.fairUsageHsmKeypairs) || 0,
        addon: Number(hsmRow.addonHsmKeypairs) || 0,
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseData])

  // Snapshot each row's initial remaining and platform allocation. This is the
  // baseline we restore to when the sig-units input is cleared and the baseline
  // used to compute platform-column deltas after update.
  useEffect(() => {
    licenseData.forEach((row: any) => {
      if (!row?.featureId) return
      if (initialByFeatureRef.current[row.featureId]) return
      const rawPlatform = row.licenseAllocation
      let platform: number | 'unlimited' | null
      if (rawPlatform === undefined || rawPlatform === null || rawPlatform === '-') {
        // Not yet populated by setupStates; skip so we can capture the real
        // value on a later render pass.
        return
      } else if (rawPlatform === translate('common.form.unlimited') || rawPlatform === UNLIMITED) {
        platform = 'unlimited'
      } else {
        const n = parseInt(rawPlatform, 10)
        platform = isNaN(n) ? null : n
      }
      initialByFeatureRef.current[row.featureId] = {
        remaining: Number(row.remaining) || 0,
        platform,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseData])

  // STM: STM's usage response only includes derived-seat features (repositories, test_signature_units)
  // when they have been allocated. On tiers/accounts where they haven't been allocated yet,
  // STM omits them from `features`. Synthesize placeholder v2.5 rows from fair-usage so users
  // can still see and preview the derived allocations. Rows are display-only (dropdown disabled).
  useEffect(() => {
    if (!isStm || !fairUsage) return
    setLicenseData((prev) => {
      const hasRepos = prev.some(r => r.featureId === 'repositories' || r.featureId === 'repositories_v25')
      const hasTestSigs = prev.some(r => r.featureId === 'test_signature_units' || r.featureId === 'test_signature_units_v25' || r.featureId === 'test_signatures')
      if (hasRepos && hasTestSigs) return prev
      const synthetic: any[] = []
      if (!hasRepos) {
        synthetic.push({
          id: `repositories_synth_${Math.random()}`,
          featureId: 'repositories',
          version: LicenseFeatureVersion.V2_5,
          licenseType: translate('dashboardLicense.features.repositories') || 'Repositories',
          remaining: 0,
          licenseAllocation: '0',
          used: '0',
          totalAllocated: 0,
        })
      }
      if (!hasTestSigs) {
        synthetic.push({
          id: `test_signature_units_synth_${Math.random()}`,
          featureId: 'test_signature_units',
          version: LicenseFeatureVersion.V2_5,
          licenseType: translate('dashboardLicense.features.test_signature_units') || 'Test signature units',
          remaining: 0,
          licenseAllocation: '0',
          used: '0',
          totalAllocated: 0,
        })
      }
      return [...prev, ...synthetic]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairUsage])

  const setupStates = async (response) => {
    if (response.ok) {
      const licenseFeature = await response.json()
      setPlatformLicense(licenseFeature)
      const data = licenseData.map((data, index) => {
        updateLimitArray[index] = data.totalAllocated.toString()
        if (licenseFeature) {
          const feature = findPlatformFeature(licenseFeature.features, data.featureId)
          if (feature) {
            data.licenseAllocation = feature.attributes.remaining === -1 ? translate('common.form.unlimited') : feature.attributes.remaining
          } else {
            data.licenseAllocation = '-'
          }
        }
        return data
      })
      setUpdateLimit(updateLimitArray)
      if (licenseFeatureVersion) {
        const licenseDataState = data.filter(license => versionMatches(license?.version, licenseFeatureVersion))
        setLicenseData(licenseDataState)
        setSelectedLicenseVersion(licenseFeatureVersion)
      } else {
        setLicenseData(data)
      }
    }
  }

  let licenseFeatures
  if (license) {
    license.applications.map(app => {
      if (app.app_code === app_code) {
        licenseFeatures = app.features
      }
    })
  }

  const checkValidation = (input: string, item: IlicenseCard, action: string) => {
    // Some apps (e.g. AI Trust) expose license attributes that are not present in the
    // signed license's feature list. Guard the lookup so a missing feature does not throw
    // (which would leave the Update button greyed out); treat it as unlimited (-1).
    const matchedLicenseFeature = licenseFeatures?.find(licenseFeature => licenseFeature.name === item.featureId)
    const licenseFeatureLimit = matchedLicenseFeature?.attributes?.limit ?? -1
    let validation = ''

    if (
      !/^\d*$/.test(input) ||
      /^0[0-9].*$/.test(input) ||
      /^\d*\.\d*$/.test(input) ||
      /^([a-zA-Z])\w+/.test(input)
    ) {
      validation = translate('dashboardLicense.validation.positiveNumber') as string
    }

    if (/^\d*$/.test(input)) {
      if (
        action === LicenseFormActions.ADD && licenseFeatureLimit !== -1 && parseInt(item.used, 10) < 0 && parseInt(input, 10) < parseInt(item.used, 10)
      ) {
        validation = translate('dashboardLicense.validation.range') + ` ${parseInt(item.used, 10)}` + ' ' + translate('common.to') + ' ' + ` ${parseInt(item.licenseAllocation, 10)}.`
      } else if (action === LicenseFormActions.ADD && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > parseInt(item.licenseAllocation, 10)) {
        validation = translate('dashboardLicense.validation.range') + ' 1 ' + translate('common.to') + ' ' + ` ${parseInt(item.licenseAllocation, 10)}.`
      }
      else if (action === LicenseFormActions.SUBTRACT && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > item.totalAllocated) {
        validation = translate('dashboardLicense.validation.range') + ' 1 ' + translate('common.to') + ' ' + ` ${item.totalAllocated}.`
      }
      else if (action === LicenseFormActions.RESET && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > item.totalAllocated + parseInt(item.licenseAllocation, 10)) {
        validation = translate('dashboardLicense.validation.range') + ' 0 ' + translate('common.to') + ' ' + ` ${item.licenseAllocation + item.totalAllocated}.`
      }
    }
    if (action === LicenseFormActions.SUBTRACT && parseInt(input, 10) > (item.totalAllocated - parseInt(item.used, 10))) {
      validation = translate('dashboardLicense.validation.cannotExceedNumber') + ` ${item.totalAllocated - parseInt(item.used, 10)}.`
    }

    // STM fair-usage: the signature-units total after update must not drop below
    // the fair-usage `signature_minimum`. Derived seats (repositories, HSM,
    // test-signatures) are computed from this floor, so going below it is invalid.
    if (isStm && isSignatureUnitsFeature(item.featureId) && fairUsage && /^\d+$/.test(input)) {
      const minimum = fairUsage.signature_minimum ?? 0
      if (minimum > 0) {
        const entered = parseInt(input, 10)
        const currentTotal = Number(item.totalAllocated) || 0
        let projectedTotal = currentTotal
        if (action === LicenseFormActions.ADD) projectedTotal = currentTotal + entered
        else if (action === LicenseFormActions.SUBTRACT) projectedTotal = currentTotal - entered
        else if (action === LicenseFormActions.RESET) projectedTotal = entered
        if (projectedTotal < minimum) {
          // Prefer a registered translation; fall back to a friendly sentence so
          // the message never renders as the raw key segment (e.g. "signatureMinimum").
          const label = translate('dashboardLicense.validation.signatureUnitsMinimum') as string
          const friendly = (!label || label === 'signatureUnitsMinimum')
            ? 'Signature units cannot be reduced below the minimum of'
            : label
          validation = `${friendly} ${minimum}.`
        }
      }
    }

    return validation
  }

  const changeTotal = (
    value: string,
    index: number,
    action: string) => {
    const licenseTemp = [...licenseRemaining]
    const accountTemp = [...accountRemaining]
    const updateLimitTemp = [...updateLimit]
    if (action === LicenseFormActions.ADD && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) - parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (licenseData[index].remaining + parseInt(value, 10)).toString()
      updateLimitTemp[index] = (licenseData[index].totalAllocated + parseInt(value, 10)).toString()
    }
    if (action === LicenseFormActions.SUBTRACT && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) + parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (licenseData[index].remaining - parseInt(value, 10)).toString()
      updateLimitTemp[index] = (licenseData[index].totalAllocated - parseInt(value, 10)).toString()
    }
    if (action === LicenseFormActions.RESET && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) + licenseData[index].totalAllocated - parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (parseInt(value, 10)).toString()
      updateLimitTemp[index] = (parseInt(value, 10)).toString()
    }
    if (!value) {
      accountTemp[index] = '-'
      licenseTemp[index] = '-'
      updateLimitTemp[index] = licenseData[index].totalAllocated.toString()
    }
    setLicenseRemaining(licenseTemp)
    setAccountRemaining(accountTemp)
    setUpdateLimit(updateLimitTemp)
  }

  const changeLicense = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    item: IlicenseCard
  ) => {
    const inputValueArray = [...inputValues]
    inputValueArray[index] = event.target.value
    setInputValues(inputValueArray)
    changeTotal(event.target.value, index, action[index])

    // STM HSM v2: reinterpret the input against the live fair value so the
    // final limit = liveFair + newAddon. addon is computed from the initial
    // API addon plus/minus the user's input (RESET means "set final limit").
    if (isStm && isHsmKeyPairFeature(item.featureId) && initialHsmRef.current) {
      const raw = event.target.value
      const parsedInput = parseInt(raw, 10)
      const validInput = raw !== '' && !isNaN(parsedInput) && parsedInput >= 0
      const initAddon = initialHsmRef.current.addon
      const liveFair = Number((licenseData[index] as any).fairUsageHsmKeypairs) || 0
      const currentAction = action[index]
      let newAddon = initAddon
      if (validInput) {
        if (currentAction === LicenseFormActions.ADD) newAddon = initAddon + parsedInput
        else if (currentAction === LicenseFormActions.SUBTRACT) newAddon = Math.max(0, initAddon - parsedInput)
        else if (currentAction === LicenseFormActions.RESET) newAddon = Math.max(0, parsedInput - liveFair)
      }
      const newLimit = liveFair + newAddon
      setLicenseData(prev => prev.map((r, i) => i === index ? { ...r, addonHsmKeypairs: newAddon } : r))
      const setAccount = (prev: string[]) => { const t = [...prev]; t[index] = newLimit.toString(); return t }
      const setLicense = (prev: string[]) => {
        const t = [...prev]
        const init = initialByFeatureRef.current[item.featureId]
        if (!init || init.platform == null) { t[index] = '-'; return t }
        if (init.platform === 'unlimited') { t[index] = translate('common.form.unlimited') as string; return t }
        const addonDelta = newAddon - (initialHsmRef.current?.addon ?? 0)
        t[index] = (init.platform - addonDelta).toString()
        return t
      }
      setUpdateLimit(setAccount)
      setAccountRemaining(setAccount)
      setLicenseRemaining(setLicense)
    }
    const ref = errorRefs[index].current
    if (ref) {
      ref.style.display = 'none'

      const validation = checkValidation(
        event.target.value,
        item, action[index]
      )

      if (validation) {
        setSubmitDisabled(true)
        ref.style.display = 'block'
      }

      const tempErrorMessage = [...errorMessages]
      tempErrorMessage[index] = validation
      setErrorMessages(tempErrorMessage)

      setSubmitDisabled(false)
      for (const item of tempErrorMessage) {
        if (item !== '') {
          setSubmitDisabled(true)
        }
      }
      if (action.every(a => a === 'no-change') && tempErrorMessage.every((error) => error !== '')) {
        setSubmitDisabled(true)
      }

      if (action.every(a => a !== 'no-change') && tempErrorMessage.every((error) => error === '')) {
        setSubmitDisabled(false)
      }
    }
  }

  const onCancel = () => {
    onCancelClick(null, {})
  }
  const getQuantity = (action, index) => {
    switch (action) {
      case LicenseFormActions.ADD:
        return parseInt(inputValues[index - 1], 10)
      case LicenseFormActions.SUBTRACT:
        return -parseInt(inputValues[index - 1], 10)
      case LicenseFormActions.RESET:
        return parseInt(inputValues[index - 1], 10) - licenseData[index - 1].remaining
      default:
        return inputValues.map(() => 0)
    }
  }

  // `old_limit` is serialized to a primitive `int` on the backend
  // (Feature.oldLimit). `totalAllocated` may be the display string "Unlimited"
  // (rendered when the license limit is -1) or "-" when no allocation data
  // exists, both of which would fail Jackson deserialization. Coerce to the
  // numeric value the backend expects: -1 for unlimited, the parsed number
  // otherwise, and 0 as a safe fallback for non-numeric placeholders.
  // The "Unlimited" label can arrive as the UNLIMITED constant or as a
  // localized string from either translation key used across the license UI
  // (`dashboardLicense.unlimited` feeds totalAllocated; `common.form.unlimited`
  // feeds the platform column), so match all three to stay locale-safe.
  const toNumericLimit = (value: unknown): number => {
    if (typeof value === 'number') return value
    if (
      value === UNLIMITED ||
      value === translate('dashboardLicense.unlimited') ||
      value === translate('common.form.unlimited')
    ) {
      return -1
    }
    const parsed = typeof value === 'string' ? parseInt(value, 10) : NaN
    return Number.isNaN(parsed) ? 0 : parsed
  }

  const onSubmit = () => {
    let index = 0
    const payload: any = {
      features: licenseData.map(feature => {
        index++
        const newLimit = parseInt(updateLimit[index - 1], 10)
        const attributes: any = { limit: newLimit }
        let featureName = feature.featureId
        let oldLimit = feature.totalAllocated
        // STM v2 HSM add-on: STM's account endpoint expects the add-on as its own
        // feature line named `addon_hsm_keypairs` with attributes.limit = <addon
        // count> (fair-usage is derived server-side from signature_units). Sending
        // it as a nested attribute inside `hsm_keypair_units` is ignored by STM's
        // verb derivation, which keys off the feature name. This only applies on
        // the v2 path; v1 license updates still send `hsm_keypair_units` as-is.
        if (isStm && isHsmKeyPairFeature(feature.featureId) && selectedLicenseVersion !== LicenseFeatureVersion.V1) {
          const fairUsageHsm = typeof (feature as any).fairUsageHsmKeypairs === 'number' ? (feature as any).fairUsageHsmKeypairs : 0
          const addonFromRow = (feature as any).addonHsmKeypairs
          // Always compute the add-on from fair + addon to avoid stale updateLimit
          // arrays. Fall back to derived when addon isn't set yet.
          const addon = typeof addonFromRow === 'number'
            ? addonFromRow
            : Math.max(0, (isNaN(newLimit) ? 0 : newLimit) - fairUsageHsm)
          featureName = 'addon_hsm_keypairs'
          oldLimit = initialHsmRef.current?.addon ?? 0
          attributes.limit = addon
          attributes.addon_hsm_keypairs = addon
          attributes.fair_usage_hsm_keypairs = fairUsageHsm
        }
        return {
          name: featureName,
          attributes,
          reset: action[index - 1] === LicenseFormActions.RESET,
          old_limit: toNumericLimit(oldLimit),
          _version: (feature as any).version,
        }
      })
        // STM computes v2.5 derived seats (repositories, test_signatures) from
        // fair-usage; sending them in PUT can cause STM to reject the payload.
        .filter((f: any) => !(isStm && f._version === LicenseFeatureVersion.V2_5))
        .map(({ _version, ...rest }: any) => rest)
    }
    payload.license_feature_version = selectedLicenseVersion;
    if (isStm && stmPackageCode) {
      payload.package = stmPackageCode;
    }

    index = 0
    const licenseEventPayload = {
      features: licenseData.map(feature => {
        index++
        if (inputValues[index - 1] != '') {
          return {
            name: feature.featureId,
            quantity: getQuantity(action[index - 1], index),
            updated_limit: accountRemaining[index - 1] !== '-' ? parseInt(accountRemaining[index - 1], 10) : feature.remaining
          }
        }
      }).filter(feat => feat != undefined)
    }
    updateLicenseByApp(payload, context, id, licenseEventPayload, app_code)
      .then((data) => {
        onCancelClick(null, {
          app_code: app_code,
          data: window.licenseData
        })
      })
      .catch((error) => {
        // The update action already surfaces the STM/backend error via the
        // global notification framework (top-right popup). Here we just keep the
        // form open and re-enable the submit button so the user can correct and
        // retry (PLFINT-1348).
        console.error('License update failed:', error)
        setSubmitDisabled(false);
      })
  }

  const hideNotification = (id: string | number) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
  }

  function onChangeAction(e, index) {
    const actionArr = [...action]
    actionArr[index] = e
    setAction(actionArr)
    const inputArray = [...inputValues]
    if (e === 'no-change') {
      inputArray[index] = ''
      setInputValues(inputArray)
      changeTotal('', index, e)
    } else {
      changeTotal(inputValues[index], index, e)
    }
    const isTrue = (currentValue) => currentValue === 'no-change';
    if (actionArr.every(isTrue)) {
      setSubmitDisabled(true)
    }
  }

  const onBtnMouseEnter = () => {
    if (submitDisabled && errorMessages.every((item) => item === '')) {
      setTooltipVisible(true)
    }
  }

  const onBtnMouseLeave = () => {
    setTooltipVisible(false)
  }

  const onSelect = (index) => {
    setTimeout(() => {
      inputRefs[index].focus()
    }, 100)
  }

  const saveInputRef = (el) => {
    if (el) {
      inputRefs.push(el)
    }
  }

  const resetAllLicensesToZero = () => {
    const inputArray = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? '' : '0'
    );

    const actionArray = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? LicenseFormActions.NO_CHANGE : LicenseFormActions.RESET
    );
    const licenseTemp = licenseData.map((data) =>
      data.featureId === hsmKeyPairFeatureId
        ? data.licenseAllocation
        : data.licenseAllocation !== UNLIMITED
          ? (
            parseInt(data.licenseAllocation, 10) +
            data.totalAllocated -
            0 // Reset to zero
          ).toString()
          : data.licenseAllocation
    );

    const accountTemp = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? item.remaining.toString() : '0'
    );

    const updateLimitTemp = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? item.totalAllocated.toString() : '0'
    );
    setInputValues(inputArray);
    setAction(actionArray);

    setLicenseRemaining(licenseTemp);
    setAccountRemaining(accountTemp);
    setUpdateLimit(updateLimitTemp);
    setSubmitDisabled(false);
  };

  const manageV2VersionSwitch = (value: boolean) => {
    const version = value ? LicenseFeatureVersion.V2 : LicenseFeatureVersion.V2_5;
    updateLicenseState(version);
  }

  const manageLicenseSwitch = (value: boolean) => {
    setV2Enabled(value)
    const version = value ? LicenseFeatureVersion.V2 : LicenseFeatureVersion.V1;
    updateLicenseState(version);
  }

  const updateLicenseState = (version: LicenseFeatureVersion) => {
    const licenseDataState = parentLicenseData.filter(license => versionMatches(license?.version, version));
    const newAccountRemainingArray = new Array<string>(licenseDataState.length).fill('-');
    const newLicenseRemainingArray = new Array<string>(licenseDataState.length).fill('-');
    const newUpdateLimitArray = new Array<string>(licenseDataState.length);
    const newDefaultErrorMessages = new Array<string>(licenseDataState.length).fill('');

    const licenseDataWithPlatformUpdate = licenseDataState.map((licenseSeat, index) => {
      newUpdateLimitArray[index] = licenseSeat.totalAllocated.toString();
      if (platformLicense) {
        const feature = findPlatformFeature(platformLicense.features, licenseSeat.featureId);
        if (feature) {
          licenseSeat.licenseAllocation =
            feature.attributes.remaining === -1
              ? translate('common.form.unlimited')
              : feature.attributes.remaining;
        } else {
          licenseSeat.licenseAllocation = '-';
        }
      }
      return licenseSeat;
    });
    setSelectedLicenseVersion(version);

    setLicenseData(licenseDataWithPlatformUpdate);
    setAccountRemaining(newAccountRemainingArray);
    setLicenseRemaining(newLicenseRemainingArray);
    setUpdateLimit(newUpdateLimitArray);
    setAction(new Array<string>(licenseDataState.length).fill('no-change'));
    setInputValues(new Array<string>(licenseDataState.length).fill(''));
    setErrorMessages(newDefaultErrorMessages);
    setSubmitDisabled(newUpdateLimitArray.every((limit) => limit === '0'));

  }

  // Compute STM derived seat values client-side from the fair-usage formula.
  const computeDerivedFromFairUsage = (units: number, fu?: IStmFairUsage | null): Record<string, number> => {
    if (!fu) return {}
    const effective = Math.max(fu.signature_minimum ?? 0, units)
    const applyRatio = (r?: IStmFairUsageRatio): number | undefined => {
      if (!r || !r.ratio_per_signatures) return undefined
      // Derived seats accrue one full block per completed ratio_per_signatures
      // (e.g. one repository per 1000 signatures). Use floor so a partial block
      // (1..999 extra signatures) does NOT grant an additional seat — a seat is
      // only added once the next full 1000-signature threshold is reached.
      const raw = Math.floor(effective / r.ratio_per_signatures) * r.ratio_units
      return r.cap != null && r.cap !== -1 ? Math.min(r.cap, raw) : raw
    }
    const applyBands = (b?: { bands: IStmFairUsageBand[] }): number | undefined => {
      if (!b?.bands?.length) return undefined
      // A band's upper bound is exclusive. STM represents "no maximum" as either
      // null or -1, so treat both as open-ended for the top band.
      const band = b.bands.find(x =>
        effective >= x.min_signatures &&
        (x.max_signatures == null || x.max_signatures === -1 || effective < x.max_signatures)
      )
      return band?.value
    }
    const repos = applyRatio(fu.repositories) ?? 0
    const testSigs = applyRatio(fu.test_signatures) ?? 0
    const hsm = applyBands(fu.hsm_keypairs) ?? 0
    return {
      repositories: repos,
      repositories_v25: repos,
      test_signatures: testSigs,
      test_signature_units: testSigs,
      test_signature_units_v25: testSigs,
      hsm_keypair_units: hsm,
      hsm_keypair_units_v2: hsm,
    }
  }

  const handleStmSignatureUnitsBlur = (rawValue: string, index: number) => {
    if (!isStm) return
    // Treat empty input as 0 entered units.
    const trimmed = (rawValue ?? '').trim()
    const parsed = trimmed === '' ? 0 : parseInt(trimmed, 10)
    if (isNaN(parsed) || parsed < 0) return

    // Derive from the post-update signature total, not just entered delta.
    const sigRow: any = licenseData[index]
    const currentAction = action[index]
    const currentTotal = Number(sigRow?.totalAllocated) || 0
    let signatureTotal = currentTotal
    if (currentAction === LicenseFormActions.ADD) {
      signatureTotal = currentTotal + parsed
    } else if (currentAction === LicenseFormActions.SUBTRACT) {
      signatureTotal = Math.max(0, currentTotal - parsed)
    } else if (currentAction === LicenseFormActions.RESET) {
      signatureTotal = parsed
    }

    const dependents = computeDerivedFromFairUsage(signatureTotal, fairUsage)
    if (Object.keys(dependents).length === 0) return

    type Upd = { newLimit: number; fair?: number; addon?: number }
    const rowUpdates: Record<number, Upd> = {}
    licenseData.forEach((row: any, i) => {
      if (row.version === LicenseFeatureVersion.V2_5) {
        const nv = dependents[row.featureId]
        if (typeof nv === 'number') rowUpdates[i] = { newLimit: nv }
      } else if (isHsmKeyPairFeature(row.featureId)) {
        const fu = dependents[row.featureId]
        if (typeof fu === 'number') {
          const addon = Number(row.addonHsmKeypairs) || 0
          rowUpdates[i] = { newLimit: fu + addon, fair: fu, addon }
        }
      }
    })

    setLicenseData((prev) => prev.map((row: any, i) => {
      const upd = rowUpdates[i]
      if (!upd) return row
      if (row.version === LicenseFeatureVersion.V2_5) {
        return { ...row, remaining: upd.newLimit, licenseAllocation: upd.newLimit.toString() }
      }
      if (isHsmKeyPairFeature(row.featureId) && typeof upd.fair === 'number') {
        return { ...row, fairUsageHsmKeypairs: upd.fair }
      }
      return row
    }))

    // "Total after update":
    //   account column = newLimit
    //   platform column = initial platform allocation - delta consumed.
    //     For HSM only the addon-delta consumes platform (fair is included in
    //     the plan). For v2.5 derived rows the whole (newLimit - initial)
    //     consumes. If platform is unlimited or untracked, show that verbatim.
    const patchAccount = (prev: string[]) => {
      const t = [...prev]
      Object.entries(rowUpdates).forEach(([k, v]) => { t[Number(k)] = v.newLimit.toString() })
      return t
    }
    const patchLicense = (prev: string[]) => {
      const t = [...prev]
      Object.entries(rowUpdates).forEach(([k, v]) => {
        const idx = Number(k)
        const row: any = licenseData[idx]
        const init = initialByFeatureRef.current[row.featureId]
        if (!init || init.platform == null) { t[idx] = '-'; return }
        if (init.platform === 'unlimited') { t[idx] = translate('common.form.unlimited') as string; return }
        let delta = 0
        if (isHsmKeyPairFeature(row.featureId) && initialHsmRef.current) {
          delta = (Number(v.addon) || 0) - initialHsmRef.current.addon
        } else {
          delta = v.newLimit - init.remaining
        }
        t[idx] = ((init.platform as number) - delta).toString()
      })
      return t
    }
    setUpdateLimit(patchAccount)
    setAccountRemaining(patchAccount)
    setLicenseRemaining(patchLicense)
  }

  return (
    <>
      <section className={styles.licenseCardForm}>
        {showToggle && <>
          <p><Translate id='dashboardLicense.switchToNewLicense' /></p>
          <Switch
            className={styles.newLicenseSwitch}
            id='newLicenseSwitch'
            checkedChildren='On'
            unCheckedChildren='Off'
            onChange={manageLicenseSwitch}
            checked={selectedLicenseVersion !== LicenseFeatureVersion.V1}
          /></>}
        {props?.app_code?.includes(Applications.MPKI) && parentLicenseData.filter(license => license?.version === LicenseFeatureVersion.V2_5).length > 0 && (v2Enabled || licenseFeatureVersion === LicenseFeatureVersion.V2_5 || licenseFeatureVersion === LicenseFeatureVersion.V2) && <>
          <p><Translate id='dashboardLicense.switchToFlex' /></p>
          <Switch
            className={styles.newLicenseSwitch}
            id='v2licenseSwitch'
            checkedChildren={'On'}
            unCheckedChildren={'Off'}
            onChange={manageV2VersionSwitch}
            checked={selectedLicenseVersion === LicenseFeatureVersion.V2}
          /></>}
        {isLicenseResetAllowed && type !== IAccessScope.partner &&
          <div className={styles.restAllLicenses}>
            <a id='resetAllLicense' onClick={resetAllLicensesToZero}>
              <Translate id='dashboardLicense.resetAllLicenses' />
            </a>
          </div>}
        <ul className={styles.licenseCardFormHeader}>
          <li><strong><Translate id='common.form.licenseType' /></strong></li>
          <li><Translate id='common.form.updateNumberOfLicenses' /></li>
          <li><Translate id='common.form.remainingAccount' /></li>
          {partnerAccountId ? <li><Translate id='common.form.remainingPartner' /></li> : <li><Translate id='common.form.remainingPlatform' /></li>}
        </ul>
        {(() => {
          // For STM v2, order rows as: signature_units, hsm_keypair_units, then v2.5 derived rows.
          let orderedIndices = licenseData.map((_, i) => i)
          if (isStm) {
            const sigIdx: number[] = []
            const hsmIdx: number[] = []
            const derivedIdx: number[] = []
            const otherIdx: number[] = []
            licenseData.forEach((row, i) => {
              if (isSignatureUnitsFeature(row.featureId)) sigIdx.push(i)
              else if (isHsmKeyPairFeature(row.featureId)) hsmIdx.push(i)
              else if (row.version === LicenseFeatureVersion.V2_5) derivedIdx.push(i)
              else otherIdx.push(i)
            })
            orderedIndices = [...sigIdx, ...hsmIdx, ...derivedIdx, ...otherIdx]
          }
          return orderedIndices.map((index) => {
          const item = licenseData[index]
          const isDerivedRow = isStm && item.version === LicenseFeatureVersion.V2_5
          const isHsmRow = isStm && isHsmKeyPairFeature(item.featureId)
          let options = [...actions]
          if (parseInt(item.licenseAllocation) === 0 && item.totalAllocated == 0 && item.remaining == 0) {
            options.splice(1, 2)
          }
          else if (item.totalAllocated == parseInt(item.used) && parseInt(item.used) > 0 && parseInt(item.licenseAllocation) == 0) {
            options.splice(1, 2)
          }
          else if (parseInt(item.licenseAllocation) === 0) {
            options.splice(1, 1)
          }
          else if (item.remaining <= 0) {
            options.splice(2, 1)
          }
          else if (typeof item.licenseAllocation == 'undefined' && item.remaining.toString() == '-') {
            options.splice(1, 2)
          }

          if (isLicenseResetAllowed && type !== IAccessScope.partner && typeof item.licenseAllocation !== 'undefined'
            && item.remaining.toString() !== '-' && item.featureId !== hsmKeyPairFeatureId) {
            options.push({
              label: translate('common.reset') as string,
              value: LicenseFormActions.RESET
            })
          }
          return (
            <div key={index} className={styles.licenseCardFormRowContainer}>
              <div className={styles.licenseCardFormRow}>
                <div>{item.licenseType}</div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <div className={styles.licenseCardFormRowItemActions}>
                    <Select
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                      defaultValue="no-change"
                      label=""
                      onChange={(event): void => { onChangeAction(event, index) }}
                      options={options}
                      size="s"
                      value={isDerivedRow ? 'no-change' : action[index]}
                      onSelect={() => onSelect(index)}
                      disabled={isDerivedRow}
                    />
                    <input
                      ref={saveInputRef}
                      type='text'
                      className={cn(styles.licenseInput, {
                        [styles.invalid]: errorMessages[index] !== ''
                      })}
                      value={inputValues[index]}
                      disabled={isDerivedRow || action[index] === 'no-change'}
                      onChange={(event): void => {
                        changeLicense(index, event, item)
                        if (isStm && isSignatureUnitsFeature(item.featureId)) {
                          handleStmSignatureUnitsBlur(event.target.value, index)
                        }
                      }}
                      onBlur={(event): void => {
                        if (isStm && isSignatureUnitsFeature(item.featureId)) {
                          handleStmSignatureUnitsBlur(event.target.value, index)
                        }
                      }}
                    />
                  </div>
                  <div className={styles.errors} ref={errorRefs[index]}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    <p className={styles.errorMessage}>{errorMessages[index]}</p>
                  </div>
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p><Translate id='common.form.totalAfterUpdate' /></p>
                </div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <p>{item.remaining}</p>
                  {isHsmRow && (item.fairUsageHsmKeypairs != null || item.addonHsmKeypairs != null) && (
                    <p style={{ fontSize: 12, color: '#6b7684', margin: '2px 0 0 0' }}>
                      {item.fairUsageHsmKeypairs ?? 0} included in plan + {item.addonHsmKeypairs ?? 0} purchased
                    </p>
                  )}
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p className={parseInt(accountRemaining[index]) > item.remaining ? styles.increased : styles.decreased}>{accountRemaining[index]}</p>
                </div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <p>{capDisplayForFeature(item.featureId) ?? item.licenseAllocation}</p>
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p className={parseInt(licenseRemaining[index]) > item.remaining ? styles.decreased : styles.increased}>{licenseRemaining[index]}</p>
                </div>
              </div>
            </div>
          )
          })
        })()}
      </section>
      <ToasterContainer hideNotification={hideNotification} notifications={notifications} />
      <div className={styles.buttonWrap}>
        <div className={styles.cancelButton}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
            <Translate id='profile.cancelBtn' />
          </Button>
        </div>
        <div
          onMouseEnter={onBtnMouseEnter}
          onMouseLeave={onBtnMouseLeave}
          style={{ display: 'inline' }}
        >
          <Tooltip
            title={<Translate id='dashboardLicense.tooltip' />}
            placement='top'
            visible={tooltipVisible}
          >
            <button
              className={cn(styles.submitButton, {
                [styles.btnDisabled]: submitDisabled
              })}
              onClick={onSubmit}
              disabled={submitDisabled}
            >
              <Translate id='common.form.buttons.updateLicense' />
            </button>
          </Tooltip>
        </div>
      </div>
    </>
  )
}
